import React from 'react';
import { captureException } from '@sentry/react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { getSingleArticle } from 'data/SingleArticle/data';
import { Article as ArticleType } from 'data/SingleArticle/types';
import Article from 'components/pages/Article/Article';
import { Meta } from 'components/Meta';
import { PreviewModeBanner } from 'components/PreviewModeBanner/PreviewModeBanner';
import { PageWrapper } from 'components/PageWrapper/PageWrapper';
import { getAllSlugs } from 'data/SingleArticle/getAllSlugs';
import { Breadcrumbs } from 'components/BreadCrumbs/Breadcrumbs';
import { articleJsonLd } from 'json-ld/articleJsonLd';
import { ZapierProductModule } from 'components/ZapierProductModule/ZapierProductModule';
import { Spacer } from 'components/Spacer/Spacer';
import { PageLoadingSpinner } from 'components/PageLoadingSpinner/PageLoadingSpinner';
import { RelatedArticles } from 'components/Grids/MiniArticleCards/RelatedArticles';
import {
  ISR_REVALIDATE_THRESHOLD,
  DISABLE_PRE_RENDERING,
} from 'constants/build-constants';
import { logDebug } from 'logging/logger';
import { EditPostBanner } from 'components/EditPostBanner/EditPostBanner';
import convertMarkdownToHtml from 'components/HTMLToMarkdown/convertMarkdownToHtml';
import useIsStaff from 'data/User/useIsStaff';
import { EMBED_TYPES } from 'data/global/constants';
import { GuidedRecipeShort } from 'data/Article/types';
import { getZapTemplatesInfo } from 'data/Article/data';
import {
  checkIfInstagramEmbed,
  checkIfTwitterEmbed,
  instagramEmbedScriptUri,
  removeInstagramEmbedTag,
  removeTwitterEmbedTag,
  twitterEmbedScriptUri,
} from 'data/SingleArticle/helpers';
import Head from 'next/head';

type Props = {
  article: ArticleType;
  preview: boolean;
  guidedRecipes: GuidedRecipeShort[];
  shouldEmbedInstagam: boolean;
  shouldEmbedTwitter: boolean;
};

export default function BlogPost(props: Props) {
  const router = useRouter();
  const isStaff = useIsStaff();
  const [upNextArticleSlug, setUpNextArticleSlug] = React.useState('');
  const [editBannerVisible, setEditBannerVisible] = React.useState(true);

  React.useEffect(() => {
    setEditBannerVisible(true);
  }, [router.asPath]);

  // This displays only while getStaticProps() requests data after which the EmptyArticlePage is rendered
  // This will rarely _ever_ be seen (even then only for a short flash) thanks to prefetch and complete getStaticPaths()
  // https://nextjs.org/docs/basic-features/data-fetching#fallback-pages
  if (router.isFallback) {
    return <PageLoadingSpinner />;
  }

  // Happy path from here on forward. Display article page!
  const { article, preview, guidedRecipes } = props;
  const { metadata, image, discoverable } = article;

  return (
    <>
      <Head>
        <script
          type="text/javascript"
          src="//s7.addthis.com/js/300/addthis_widget.js#pubid=ra-5f36e19bf3803e41"
        />
        {props.shouldEmbedInstagam && (
          <script async src={instagramEmbedScriptUri} />
        )}
        {props.shouldEmbedTwitter && (
          <script async src={twitterEmbedScriptUri} />
        )}
      </Head>

      {isStaff && editBannerVisible && (
        <EditPostBanner
          articleId={article.id}
          handleHideBanner={() => setEditBannerVisible(false)}
        />
      )}
      <PageWrapper>
        <Meta
          title={metadata.seoTitle}
          description={metadata.seoDescription}
          image={metadata.socialImage?.image?.url || image.url}
          discoverable={discoverable}
          jsonLd={articleJsonLd({ article })}
        />
        {preview && <PreviewModeBanner />}
        <Breadcrumbs breadcrumbs={article.categories} />
        <Article
          article={article}
          guidedRecipes={guidedRecipes}
          sendUpNextArticleSlugToParent={setUpNextArticleSlug}
        />
        <RelatedArticles
          article={article}
          key={article.slug}
          slugsToDedupe={[upNextArticleSlug]}
        />
        <Spacer hasLine={false} />
      </PageWrapper>
      <ZapierProductModule />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const articles = await getAllSlugs();

  return {
    paths: DISABLE_PRE_RENDERING
      ? []
      : articles.map(({ slug }) => ({ params: { slug } })),
    // Allow paths which aren't declared statically
    // This enables new blog posts to be added and supports previewing unpublished posts
    // https://nextjs.org/docs/basic-features/data-fetching#fallback-pages
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<Props, any> = async (context) => {
  const { slug } = context.params;
  const { preview = false } = context;
  let guidedRecipes: GuidedRecipeShort[] = [];

  logDebug(`[slug].tsx getStaticProps() ${slug}`);

  let shouldEmbedInstagam = false;
  let shouldEmbedTwitter = false;

  let article: ArticleType;
  try {
    article = await getSingleArticle(slug, preview);
  } catch (e) {
    captureException(e);
    return {
      revalidate: ISR_REVALIDATE_THRESHOLD,
      notFound: true,
    };
  }

  const zapierTemplateIds: number[] = [];

  for (const block of article.body.links.entries.block) {
    if (block?.__typename === 'Markdown' && block.markdown) {
      block.markdown = convertMarkdownToHtml(block.markdown);

      if (checkIfInstagramEmbed(block.markdown)) {
        shouldEmbedInstagam = true;
        block.markdown = removeInstagramEmbedTag(block.markdown);
      } else if (checkIfTwitterEmbed(block.markdown)) {
        shouldEmbedTwitter = true;
        block.markdown = removeTwitterEmbedTag(block.markdown);
      }
    }
    if (
      block?.__typename === EMBED_TYPES.ZAPIER_TEMPLATE &&
      block?.zapTemplateIDs
    ) {
      block.zapTemplateIDs = block?.zapTemplateIDs.reduce<string[]>(
        (reducedIds, currentId) => {
          if (!currentId) return reducedIds;

          const id = currentId.trim();
          reducedIds.push(id);

          const zapTemplateId = Number(id);

          /**
           *  We want to fail the build if we have a zapier template id that isn't a number.
           * Per NextJS docs, if there is an error when building with ISR, the old page will be served - https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration
           */
          if (isNaN(zapTemplateId))
            throw new Error(`Invalid ZapTemplate ID: ${id}`);

          const isAlreadyIncluded = zapierTemplateIds.some(
            (templateId) => templateId === zapTemplateId,
          );

          if (!isAlreadyIncluded) {
            zapierTemplateIds.push(zapTemplateId);
          }

          return reducedIds;
        },
        [],
      );
    }
  }

  if (zapierTemplateIds.length) {
    const ids = zapierTemplateIds.map((id) => Number(id));
    guidedRecipes = await getZapTemplatesInfo({ ids });
  }

  return {
    props: {
      article,
      preview,
      slug,
      guidedRecipes,
      shouldEmbedInstagam,
      shouldEmbedTwitter,
    },
    /**
     * Periodically revalidate content and rebuild page if content has changed
     * https://nextjs.org/docs/basic-features/data-fetching#incremental-static-regeneration
     */
    revalidate: ISR_REVALIDATE_THRESHOLD,
  };
};
